// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useShare from "@/hooks/useShare";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import type { TyLead, TyReservation, TyStatusLock, TyUnit, TyUnitHolder, TyUnitStatus, TyWarranty } from "@/types";
import { formatPrice, readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import StatusChip from "@/UI/atoms/StatusChip";
import LeadCard from "@/UI/cards/LeadCard";
import UnitHolderCard from "@/UI/cards/UnitHolderCard";
import ActionsBar from "@/UI/components/ActionsBar";
import AddMenuButton from "@/UI/components/AddMenuButton";
import EmptyState from "@/UI/components/EmptyState";
import RecordFiles from "@/UI/components/RecordFiles";
import RecordWarranties from "@/UI/components/RecordWarranties";
import RecordPanel from "@/UI/components/RecordPanel";
import UnitStepper from "@/UI/components/UnitStepper";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import CloseDealPopup from "@/UI/popups/CloseDealPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import DealPopup, { type TyDeal } from "@/UI/popups/DealPopup";
import HistoryPopup from "@/UI/popups/HistoryPopup";
import LeadFormPopup from "@/UI/popups/LeadFormPopup";
import UnitFormPopup from "@/UI/popups/UnitFormPopup";
import UploadFilesPopup from "@/UI/popups/UploadFilesPopup";
import WarrantyFormPopup from "@/UI/popups/WarrantyFormPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type TyTab = "details" | "interests" | "warranties" | "files";

/**
 * One unit — the densest screen in the product, and the reason the schema groups its attributes.
 *
 * **Every group renders from `constants.unitGroups`.** Fourteen counts, fifteen features and three
 * measures are drawn by three `map`s, so an attribute added to the model appears here without anyone
 * writing a line of markup for it.
 *
 * Only what is **known** is shown. `null` means nobody said, and printing "0 bedrooms" for a unit
 * nobody has filled in yet would be the screen inventing a fact.
 *
 * The project is the page's context, not one of its figures: it says where you are, at the top, with
 * a way back into it — a number in a row of numbers said nothing about belonging.
 */
export default function UnitScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const { slug = "", number = "" } = useParams();
    const addComp = usePopup((state) => state.addComp);
    const { canShare, share } = useShare();
    const { role } = usePermission();
    const uploadsFinishedAt = useUploads((state) => state.finishedAt);
    const [params, setParams] = useSearchParams();

    const tab = (params.get("tab") as TyTab) || "details";

    // The number is only unique inside its project, so the project travels with it — see the
    // backend's `findProperty`. Mutations use the record's own id once it has been read.
    const unit = useFetch<{ unit: TyUnit; statusLock: TyStatusLock; holder: TyUnitHolder | null }>(
        `/properties/units/${encodeURIComponent(number)}`,
        {
            query: { project: slug },
        }
    );
    const action = useFetch("/properties/units", {}, false);
    /** Who wants this one. The pipeline's own list, filtered to this unit — see `crm/leads`. */
    const leads = useFetch<{ leads: TyLead[] }>("/crm/leads", {}, false);
    /** Its own call: after a delete there is no record left to reload, so the screen leaves instead. */
    const removal = useFetch("/properties/units", {}, false);

    useEffect(() => {
        if (!action.succeededAt) return;
        unit.invoke();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    useEffect(() => {
        if (!uploadsFinishedAt) return;
        unit.invoke();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadsFinishedAt]);

    useEffect(() => {
        if (!removal.succeededAt) return;
        navigate(`/properties/${slug}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removal.succeededAt]);

    const unitId = unit.data?.unit._id;

    useEffect(() => {
        if (tab !== "interests" || !unitId) return;
        leads.invoke({ query: { unit: unitId, closed: "all", limit: constants.maxPageSize } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, unitId]);

    const record = unit.data?.unit;
    const statusLock = unit.data?.statusLock ?? null;
    const holder = unit.data?.holder ?? null;
    const project = record && typeof record.project === "object" ? record.project : null;
    const building = project?.buildings?.find((entry) => entry._id === record?.building) ?? null;

    if (unit.loading && !record) return <p className="unit-screen__state">{t.common.loading}</p>;
    if (unit.error) return <p className="unit-screen__state unit-screen__state--error">{unit.error}</p>;
    if (!record) return null;

    const refresh = () => unit.invoke();

    /** A unit has a public page only when **both** it and its project are published. */
    const isShareable = !!record.isPublic && !!project?.isPublic && canShare;

    /**
     * A step on the trail.
     *
     * **Only `available` is a status change.** The other two are derived — a unit is `reserved`
     * because a reservation holds it and `sold` because a contract does — so pressing one opens the
     * deal form rather than writing a word, exactly as pressing `won` on a lead does. The form is
     * the same one the pipeline uses, opened from the other end: it already knows the unit, so the
     * question it asks is who.
     *
     * Putting a unit back on the shelf is the one direction that needs no paperwork, because the
     * stepper is dead whenever any exists — see `UnitStepper`.
     */
    const moveStatus = (status: TyUnitStatus) => {
        if (status === "available") {
            action.invoke({ method: "PATCH", path: `/properties/units/${record._id}`, data: { status } });
            return;
        }

        addComp({
            name: "close-deal",
            Component: CloseDealPopup,
            anim: "scale",
            details: {
                unit: record,
                mode: status === "reserved" ? "reserve" : "sell",
                type: status === "rented" ? "rent" : "sale",
                onDone: refresh,
            },
        });
    };

    /**
     * The live paper, read whole.
     *
     * **This screen has no contracts tab any more.** A flat carries at most one live contract and
     * one live hold, and the badge above the tabs already names both; a tab listing them restated
     * the badge and then buried the cancelled history of a unit underneath it — which is a question
     * about the people who walked away, and belongs on their own pages. So the badge opens the
     * record, and the hold that preceded the contract travels inside it.
     */
    const openHolder = () => {
        if (!holder) return;

        const unitRef = { _id: record._id, number: record.number, price: record.price, project: project ?? null };
        const shared = {
            _id: holder.record._id,
            unit: unitRef,
            customer: holder.record.customer,
            lead: null,
            isLive: true,
            createdBy: holder.record.createdBy ?? null,
            createdAt: holder.record.createdAt ?? "",
        };

        const deal: TyDeal =
            holder.kind === "contract"
                ? {
                      kind: "contract",
                      ...shared,
                      type: holder.record.type ?? "sale",
                      reservation: holder.record.reservation ?? null,
                      amount: holder.record.amount ?? null,
                      signedAt: holder.record.signedAt ?? null,
                      startsAt: holder.record.startsAt ?? null,
                      endsAt: holder.record.endsAt ?? null,
                  }
                : {
                      kind: "reservation",
                      ...shared,
                      price: holder.record.price ?? null,
                      deposit: holder.record.deposit ?? null,
                      expiresAt: holder.record.expiresAt ?? null,
                      status: (holder.record.status as TyReservation["status"]) ?? "active",
                  };

        addComp({
            name: "deal",
            Component: DealPopup,
            anim: "scale",
            details: {
                deal,
                listPrice: record.price,
                onCancel: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: holder.kind === "contract" ? t.sales.cancelContract : t.sales.cancelReservation,
                            message: holder.kind === "contract" ? t.sales.confirmCancelContract : t.sales.confirmCancelReservation,
                            confirmLabel: holder.kind === "contract" ? t.sales.cancelContract : t.sales.cancelReservation,
                            onConfirm: () =>
                                action.invoke({
                                    method: "PATCH",
                                    path: `/sales/${holder.kind}s/${holder.record._id}/cancel`,
                                    data: { reason: "other" },
                                }),
                        },
                    }),
            },
        });
    };

    /** The one destructive action, shared by the header's trash and the menu that can replace it. */
    const confirmRemove = () =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.common.remove,
                message: t.properties.confirmRemoveUnit.replace("{name}", record.number),
                confirmLabel: t.common.remove,
                onConfirm: () => removal.invoke({ method: "DELETE", path: `/properties/units/${record._id}` }),
            },
        });
    /** The upload action lives with the bar's add button, pointed at this unit. */
    const openUpload = (model: string, ownerId: string) =>
        addComp({ name: "upload-files", Component: UploadFilesPopup, place: "end", anim: "endSlide", details: { owner: { model, id: ownerId } } });
    const saveWarranties = (next: typeof record.warranties) =>
        action.invoke({ method: "PATCH", path: `/properties/units/${record._id}`, data: { warranties: next } });

    /**
     * **Templates are editable only while the unit is unsold.** Once a contract exists they have
     * already been copied into live records, so changing one here would alter nothing a buyer holds
     * while looking exactly as though it had — see §8 of `ai.plans/warranty-notification-models.md`.
     */
    const isCovering = record.status !== "sold" && record.status !== "rented";

    const openWarrantyForm = (warranty?: TyWarranty) =>
        addComp({
            name: "warranty-form",
            Component: WarrantyFormPopup,
            anim: "scale",
            details: { unitId: record._id, warranties: record.warranties, warranty: warranty ?? null, onDone: refresh },
        });

    /**
     * Only what somebody filled in, group by group. A unit has fourteen counts, fifteen flags and
     * three measures; printing an em-dash for every one nobody answered buries the handful that were
     * answered, and a blank already has a home — the edit panel.
     *
     * An empty group is not rendered at all, title included: a heading over nothing is still nothing.
     */
    const price = record.price ? formatPrice(record, t) : "";
    const allBasics: TyFact[] = [
        { key: "price", icon: "subscription", label: t.properties.fields.price, value: price },
        { key: "building", icon: "building", label: t.properties.fields.building, value: building?.name ?? "" },
        { key: "floor", icon: "properties", label: t.properties.fields.floor, value: record.floor ?? "" },
    ];
    const basics = allBasics.filter((fact) => fact.value !== "" && fact.value !== null);

    const measures = constants.unitGroups.measures
        .filter((key) => record.measures[key] !== null)
        .map((key) => ({ key, label: t.properties.measures[key], value: `${record.measures[key]} ${t.properties.sqm}` }));

    const counts = constants.unitGroups.counts
        .filter((key) => record.counts[key] !== null)
        .map((key) => ({ key, label: t.properties.counts[key], value: String(record.counts[key]) }));

    const flags = constants.unitGroups.flags.filter((key) => record.flags[key] !== null);

    const allChoices: TyFact[] = [
        {
            key: "acSystem",
            icon: "refresh",
            label: t.properties.choices.acSystem,
            value: record.choices.acSystem ? t.properties.acSystems[record.choices.acSystem] : "",
        },
        {
            key: "facades",
            icon: "properties",
            label: t.properties.choices.facades,
            value: record.choices.facades.map((item) => t.properties.facades[item]).join("، "),
        },
        { key: "usage", icon: "building", label: t.properties.fields.usage, value: record.usage ? t.properties.usages[record.usage] : "" },
        { key: "deedNumber", icon: "document", label: t.properties.fields.deedNumber, value: record.deedNumber || "" },
    ];
    const choices = allChoices.filter((fact) => fact.value);

    const description = readLangValue(record.description, lang);
    const hasDetails =
        basics.length > 0 || measures.length > 0 || counts.length > 0 || flags.length > 0 || choices.length > 0 || !!description || !!record.notes;

    /**
     * The pipeline's own form, with this unit **and** its project already filled in. A lead filed
     * against a unit that belongs to another project is not a thing, so both travel together.
     */
    const openAddLead = () =>
        addComp({
            name: "lead-form",
            Component: LeadFormPopup,
            place: "end",
            anim: "endSlide",
            details: {
                projectId: project?._id ?? null,
                unitId: record._id,
                onDone: () => leads.invoke({ query: { unit: record._id, closed: "all", limit: constants.maxPageSize } }),
            },
        });

    const add =
        tab === "interests"
            ? { label: t.common.add, options: [{ key: "lead", label: t.crm.addLead, icon: "crm" as const, onSelect: openAddLead }] }
            : tab === "warranties" && isCovering
              ? {
                    // "Add", like every other bar in the app. The menu underneath says what is added.
                    label: t.common.add,
                    options: [
                        {
                            key: "warranty",
                            label: t.properties.addWarranty,
                            icon: "crown" as const,
                            onSelect: () => openWarrantyForm(),
                        },
                    ],
                }
              : tab === "files"
                ? {
                      label: t.drive.upload,
                      options: [{ key: "upload", label: t.drive.upload, icon: "upload" as const, onSelect: () => openUpload("Unit", record._id) }],
                  }
                : null;

    return (
        <section className="unit-screen">
            <RecordPanel
                title={record.number}
                // Where you are: unit X, inside project Y, with the way back into it.
                context={
                    project ? (
                        <>
                            <Icon name="properties" size={14} />
                            <Link className="unit-screen__crumb" to={`/properties/${project.slug}`}>
                                {readLangValue(project.name, lang) || t.properties.unnamed}
                            </Link>
                        </>
                    ) : undefined
                }
                /* The status is **not** here any more: the trail below says it and says what comes
                   next, and a chip repeating it in the header was the same word twice on one screen. */
                chips={
                    <>
                        {record.type && <StatusChip tone="neutral" size="sm" label={t.properties.unitTypes[record.type]} />}
                        {record.offer && <StatusChip tone="accent" size="sm" label={t.properties.offers[record.offer]} />}
                        {record.incomplete && <StatusChip tone="reserved" size="sm" label={t.properties.incomplete} />}
                    </>
                }
                onHistory={
                    role === "member"
                        ? undefined
                        : () =>
                              addComp({
                                  name: "history",
                                  Component: HistoryPopup,
                                  place: "end",
                                  anim: "endSlide",
                                  details: { title: record.number, path: `/properties/units/${record._id}/updates` },
                              })
                }
                onEdit={() =>
                    project &&
                    addComp({
                        name: "unit-form",
                        Component: UnitFormPopup,
                        place: "end",
                        anim: "endSlide",
                        // The form needs the project for its buildings, and the lock so it can say why
                        // the status is not editable rather than just grey it out.
                        details: { unit: record, project, statusLock, onDone: refresh },
                    })
                }
                /*
                 * Published, so there is a link to share — and `onMore` replaces the header's own
                 * trash, which is why the delete moves in beside it rather than sitting outside a
                 * menu with one item in it.
                 */
                onMore={
                    isShareable
                        ? (anchor) =>
                              addComp({
                                  name: "unit-actions",
                                  Component: ActionsPopup,
                                  place: "anchor-below",
                                  anim: "scaleTop",
                                  cover: "invisible",
                                  details: {
                                      actions: [
                                          {
                                              key: "share",
                                              label: t.common.share,
                                              icon: "copy",
                                              onSelect: () => share(`${t.properties.fields.number} ${record.number}`, project!.slug, record.number),
                                          },
                                          { key: "remove", label: t.common.remove, icon: "trash", danger: true, onSelect: confirmRemove },
                                      ] as TyAction[],
                                  },
                                  anchor: {
                                      top: anchor.top,
                                      left: anchor.left,
                                      right: anchor.right,
                                      bottom: anchor.bottom,
                                      width: anchor.width,
                                      height: anchor.height,
                                  },
                              })
                        : undefined
                }
                onRemove={isShareable ? undefined : confirmRemove}
            >
                <ActionsBar
                    tabs={[
                        { key: "details", label: t.properties.tabs.details },
                        { key: "interests", label: t.crm.interests },
                        { key: "warranties", label: t.properties.tabs.warranties },
                        { key: "files", label: t.properties.tabs.files },
                    ]}
                    activeTab={tab}
                    onTab={(key) => setParams(new URLSearchParams({ tab: key }), { replace: true })}
                    after={add ? <AddMenuButton name="unit-add" label={add.label} options={add.options} compact /> : undefined}
                />
            </RecordPanel>

            {/* Where it has got to, and who has it — above the tabs, because both govern everything
                else on the screen and are true whichever tab somebody is standing on. The badge is
                also the only way into the paperwork: this screen has no contracts tab. */}
            <UnitStepper unit={record} statusLock={statusLock} onPick={moveStatus} disabled={action.loading} />
            {holder && <UnitHolderCard holder={holder} listPrice={record.price} onOpen={openHolder} />}
            {(action.error || removal.error) && <p className="unit-screen__state unit-screen__state--error">{action.error ?? removal.error}</p>}

            {/* --- Details: the groups that have something in them ---------------------------- */}
            {tab === "details" && (
                <div className="unit-screen__groups">
                    {/* The price, the building and the floor: the three things anyone asks about a
                        unit, and plain details rather than statistics. */}
                    {basics.length > 0 && <FactGroup icon="stat" title={t.properties.groups.basics} facts={basics} />}
                    {measures.length > 0 && <FactGroup icon="model" title={t.properties.groups.measures} facts={measures} />}
                    {counts.length > 0 && <FactGroup icon="stat" title={t.properties.groups.counts} facts={counts} />}

                    {flags.length > 0 && (
                        <section className="unit-screen__group">
                            <h2 className="unit-screen__group-title">{t.properties.groups.flags}</h2>
                            <div className="unit-screen__features">
                                {flags.map((key) => (
                                    <span key={key} className={`unit-screen__feature unit-screen__feature--${record.flags[key] ? "yes" : "no"}`}>
                                        <Icon name={record.flags[key] ? "check" : "close"} size={13} />
                                        {t.properties.flags[key]}
                                    </span>
                                ))}
                            </div>
                        </section>
                    )}

                    {choices.length > 0 && <FactGroup icon="settings" title={t.properties.groups.choices} facts={choices} />}

                    {description && <p className="unit-screen__description">{description}</p>}

                    {/* Notes are written, not recorded: the box is a page's worth of room so it reads
                        like somewhere to think rather than another field with a value in it. */}
                    {record.notes && (
                        <section className="unit-screen__group">
                            <h2 className="unit-screen__group-title">{t.properties.fields.notes}</h2>
                            <p className="unit-screen__notes">{record.notes}</p>
                        </section>
                    )}

                    {!hasDetails && <EmptyState icon="document" title={t.properties.emptyDetails} description={t.properties.emptyDetailsHint} />}
                </div>
            )}

            {/* --- Who wants it --------------------------------------------------------------- */}
            {tab === "interests" && (
                <div className="unit-screen__interests">
                    {leads.loading && <p className="unit-screen__state">{t.common.loading}</p>}
                    {!leads.loading && !leads.data?.leads.length && (
                        <EmptyState
                            span
                            icon="crm"
                            title={t.crm.emptyUnitLeads}
                            description={t.crm.emptyUnitLeadsHint}
                            actionLabel={t.crm.addLead}
                            onAction={openAddLead}
                        />
                    )}
                    {(leads.data?.leads ?? []).map((lead) => (
                        <LeadCard key={lead._id} lead={lead} onOpen={() => navigate(`/crm/leads/${lead._id}`)} />
                    ))}
                </div>
            )}

            {/* --- Warranties: what it carries, or what its sale turned them into -------------- */}
            {tab === "warranties" && (
                <>
                    {!isCovering && <p className="unit-screen__borrowed">{t.warranties.lockedBySale}</p>}
                    <RecordWarranties
                        basePath="/properties"
                        unitId={record._id}
                        templates={record.warranties}
                        reloadAt={action.succeededAt}
                        onAddTemplate={isCovering ? () => openWarrantyForm() : undefined}
                        onEditTemplate={isCovering ? (warranty) => openWarrantyForm(warranty) : undefined}
                        onRemoveTemplate={
                            isCovering
                                ? (warranty) =>
                                      addComp({
                                          name: "confirm",
                                          Component: ConfirmPopup,
                                          anim: "scale",
                                          details: {
                                              title: t.common.remove,
                                              message: t.properties.confirmRemoveWarranty,
                                              confirmLabel: t.common.remove,
                                              onConfirm: () => saveWarranties(record.warranties.filter((item) => item._id !== warranty._id)),
                                          },
                                      })
                                : undefined
                        }
                    />
                </>
            )}

            {/* --- Gallery and files, the drive's own rows shown where the unit is ------------ */}
            {tab === "files" && (
                <>
                    {record.useProjectGallery && !record.gallery.length && (
                        <p className="unit-screen__borrowed">{t.properties.fields.useProjectGallery}</p>
                    )}
                    <RecordFiles ownerModel="Unit" ownerId={record._id} onUpload={() => openUpload("Unit", record._id)} />
                </>
            )}
        </section>
    );
}

type TyFact = { key: string; label: string; value: string | number; icon?: TyIconName };

/**
 * A titled card of label/value pairs. Five groups drew the same markup; now they pass it their list.
 *
 * The badge takes the group's drawing unless a fact names its own. Fourteen counts do not each
 * deserve a different icon — they are all counts, and pretending otherwise is noise — but a price
 * and a deed number inside the same group are different kinds of fact and say so.
 */
function FactGroup({ title, icon, facts }: { title: string; icon: TyIconName; facts: TyFact[] }) {
    return (
        <section className="unit-screen__group">
            <h2 className="unit-screen__group-title">{title}</h2>
            <div className="unit-screen__facts">
                {facts.map((fact) => (
                    <div key={fact.key} className="unit-screen__fact">
                        <span className="unit-screen__fact-icon">
                            <Icon name={fact.icon ?? icon} size={15} />
                        </span>
                        <span className="unit-screen__fact-label">{fact.label}</span>
                        <strong className="unit-screen__fact-value">{fact.value}</strong>
                    </div>
                ))}
            </div>
        </section>
    );
}
